// Public reset page — reads ?token=, sets a new password with strength meter.
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrengthMeter } from '../components/custom/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset password');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-teal-500/60 transition-colors';

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-8">
        {done ? (
          <div className="text-center" data-testid="reset-password-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white">Password updated</h2>
            <p className="text-white/50 text-sm mt-2">Taking you to sign in…</p>
            <Link to="/login" className="text-teal-400 hover:text-teal-300 text-sm font-semibold mt-3 inline-block"
              data-testid="reset-go-login">Sign in now</Link>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-password-no-token">
            <h2 className="text-xl font-bold text-white">Invalid reset link</h2>
            <p className="text-white/50 text-sm mt-2">This link is missing its token. Request a new one below.</p>
            <Link to="/forgot-password" className="text-teal-400 hover:text-teal-300 text-sm font-semibold mt-4 inline-block">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Set a new password</h2>
                <p className="text-white/40 text-xs mt-0.5">Minimum 8 characters — make it a strong one.</p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">New password</label>
                <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password" required autoFocus minLength={8} className={inputCls}
                  data-testid="reset-new-password-input" />
                <PasswordStrengthMeter password={newPw} />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Confirm new password</label>
                <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password" required minLength={8} className={inputCls}
                  data-testid="reset-confirm-password-input" />
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm" data-testid="reset-error">
                  {error}{' '}
                  <Link to="/forgot-password" className="underline hover:text-red-300">Request a new link</Link>
                </div>
              )}
              <button type="submit" disabled={saving || !newPw || !confirmPw}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-40 text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="reset-submit-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Saving…' : 'Update password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
